import { useState, useEffect } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { sectionPath } from "../utils/routePaths";
import { resolveSlug } from "../utils/slugify";
import { optimizedImageUrl } from "../utils/cloudinaryUrl";

function SkeletonCard({ big }) {
  return (
    <div style={{
      borderRadius: 20,
      overflow: "hidden",
      background: "var(--bg-surface-alt)",
      animation: "shimmer 1.5s infinite",
      height: big ? "100%" : "calc(50% - 10px)",
      minHeight: big ? 500 : 220,
    }} />
  );
}

function BigCard({ place }) {
  const [hovered, setHovered] = useState(false);
  const { language } = useLanguage();

  const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  };

  const rawDesc = language === "ka" && place.descriptionKa ? place.descriptionKa : place.description;
  const shortDesc = stripHtml(rawDesc).slice(0, 130) + "…";

  const formatViews = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n || 0;

  return (
    <Link
      to={sectionPath(language, "place", resolveSlug(place, language))}
      style={{ textDecoration: "none", display: "block", height: "100%", borderRadius: 20, overflow: "hidden", position: "relative", background: "#0a0c12" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {place.image && (
        <img
          src={optimizedImageUrl(place.image, 1000)}
          alt={place.name}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transform: hovered ? "scale(1.04)" : "scale(1)", transition: "transform 0.5s ease" }}
        />
      )}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(5,8,15,0.92) 0%, rgba(5,8,15,0.4) 50%, transparent 100%)" }} />

      {/* Category pill top-left */}
      <div style={{ position: "absolute", top: 20, left: 20, display: "flex", alignItems: "center", gap: 6, background: "rgba(10,12,20,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "6px 14px" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 14, color: "#fff", fontVariationSettings: "'FILL' 1" }}>landscape</span>
        <span style={{ fontFamily: "Geist, sans-serif", fontSize: 12, fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {language === "ka" && place.categoryKa ? place.categoryKa : place.category}
        </span>
      </div>

      {/* Bottom content */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: 28 }}>
        <h3 style={{ fontFamily: "Geist, sans-serif", fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 700, color: "#fff", margin: "0 0 10px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          {language === "ka" && place.nameKa ? place.nameKa : place.name}
        </h3>

        {shortDesc && (
          <p style={{ fontFamily: "Geist, sans-serif", fontSize: 14, color: "rgba(255,255,255,0.65)", lineHeight: 1.6, margin: "0 0 20px", maxWidth: 420 }}>
            {shortDesc}
          </p>
        )}

        <button style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#fff", color: "#0a0c12", border: "none", borderRadius: 999, padding: "12px 24px", fontFamily: "Geist, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
          {language === "ka" ? "წაიკითხეთ მეტი" : "Read more"}
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_forward</span>
        </button>
      </div>
    </Link>
  );
}

function SmallCard({ place }) {
  const [hovered, setHovered] = useState(false);
  const { language } = useLanguage();

  const formatViews = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n || 0;

  const tags = [
    place.category && `#${(place.category || "").toLowerCase().replace(/\s/g, "")}`,
    place.name && `#${(language === "ka" && place.nameKa ? place.nameKa : place.name).toLowerCase().replace(/\s/g, "")}`,
    place.location && `#${(place.location || "").toLowerCase().replace(/\s/g, "")}`,
  ].filter(Boolean).slice(0, 3);

  return (
    <Link
      to={sectionPath(language, "place", resolveSlug(place, language))}
      style={{ textDecoration: "none", display: "block", borderRadius: 20, overflow: "hidden", position: "relative", background: "#0a0c12", flex: 1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {place.image && (
        <img
          src={optimizedImageUrl(place.image, 700)}
          alt={place.name}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transform: hovered ? "scale(1.05)" : "scale(1)", transition: "transform 0.5s ease" }}
        />
      )}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(5,8,15,0.9) 0%, rgba(5,8,15,0.3) 55%, transparent 100%)" }} />

      {/* Top row: category pill + bookmark */}
      <div style={{ position: "absolute", top: 16, left: 16, right: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(10,12,20,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "5px 12px" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 13, color: "#fff", fontVariationSettings: "'FILL' 1" }}>landscape</span>
          <span style={{ fontFamily: "Geist, sans-serif", fontSize: 11, fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {language === "ka" && place.categoryKa ? place.categoryKa : place.category}
          </span>
        </div>
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: "#fff" }}>bookmark</span>
        </div>
      </div>

      {/* Bottom content */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "16px 20px" }}>
        <h3 style={{ fontFamily: "Geist, sans-serif", fontSize: 18, fontWeight: 700, color: "#fff", margin: "0 0 10px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
          {language === "ka" && place.nameKa ? place.nameKa : place.name}
        </h3>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {tags.map((tag, i) => (
              <span key={i} style={{ fontFamily: "Geist, sans-serif", fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{tag}</span>
            ))}
          </div>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: "#0a0c12" }}>arrow_forward</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function LatestDiscoveries() {
  const { language, t } = useLanguage();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const snapshot = await getDocs(
          query(collection(db, "places"), orderBy("createdAt", "desc"), limit(3))
        );
        setPlaces(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error loading latest places:", err);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (!loading && places.length === 0) return null;

  const [featured, ...rest] = places;

  return (
    <section style={{ background: "transparent" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "96px 64px", boxSizing: "border-box" }} className="latest-section">

        {/* Header */}
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="#4a7fd4" strokeWidth="1.8" fill="none" />
              <path d="M12 7v5l3 3" stroke="#4a7fd4" strokeWidth="1.8" strokeLinecap="round" fill="none" />
            </svg>
            <span style={{ fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--info-text)" }}>
              {t("latest_eyebrow")}
            </span>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
            <div>
              <h2 style={{ fontFamily: "Geist, sans-serif", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text-primary)", margin: "0 0 10px", lineHeight: 1.1 }}>
                {t("latest_title")}
              </h2>
              <p style={{ fontFamily: "Geist, sans-serif", fontSize: 15, color: "var(--text-secondary)", margin: 0, maxWidth: 460 }}>
                {language === "ka"
                  ? "მოგზაურობის გზამკვლევები, ადგილობრივი რჩევები და სვანეთის ისტორიები"
                  : "Travel guides, local tips and stories from Svaneti"}
              </p>
            </div>

            <Link
              to={sectionPath(language, "categories")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "Geist, sans-serif", fontSize: 14, fontWeight: 600, color: "var(--text-primary)", textDecoration: "none", borderBottom: "2px solid var(--text-primary)", paddingBottom: 2, transition: "opacity 0.2s", whiteSpace: "nowrap" }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "0.6"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
            >
              {t("latest_view_all")}
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_forward</span>
            </Link>
          </div>
        </div>

        {/* Bento grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 20, height: 560 }} className="latest-bento">
          {loading ? (
            <>
              <SkeletonCard big />
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <SkeletonCard />
                <SkeletonCard />
              </div>
            </>
          ) : (
            <>
              {featured && <BigCard place={featured} />}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {rest.slice(0, 2).map((place) => (
                  <SmallCard key={place.id} place={place} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Bottom pill button */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: 40 }}>
          <Link
            to={sectionPath(language, "categories")}
            style={{ display: "inline-flex", alignItems: "center", gap: 10, border: "1.5px solid var(--text-primary)", color: "var(--text-primary)", padding: "14px 32px", borderRadius: 999, fontFamily: "Geist, sans-serif", fontSize: 15, fontWeight: 600, textDecoration: "none", transition: "all 0.2s" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-inverse)"; e.currentTarget.style.color = "var(--fg-inverse)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-primary)"; }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>menu_book</span>
            {language === "ka" ? "ყველა ბლოგის ნახვა" : "View all posts"}
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_forward</span>
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @media (max-width: 768px) {
          .latest-section { padding: 60px 20px !important; }
          .latest-bento { grid-template-columns: 1fr !important; height: auto !important; }
          .latest-bento > * { min-height: 320px !important; }
        }
      `}</style>
    </section>
  );
}
